import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Button from "../../Components/Button";
import CommonInput from "../../Components/Input";
import Select from "../../Components/Select";
import { useToast } from "../../Components/Toast";
import { hasAccessToken, setAccessToken } from "../../Services/SessionStore";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchPublicKey, loginUser } from "../../Store/Reducers/AuthSlice";
import {
  clearWizardState,
  completeOnboardingWizard,
  loadOnboardingWizard,
  WizardAccount,
} from "../../Store/Reducers/EasyOnboardingSlice";
import {
  ACCOUNT_TEAM_ALLOWED_ROLES,
  ACCOUNT_TEAM_ROLE_DISPLAY_LABELS,
  getDefaultRouteForRole,
  isOnboardingAccountTeamHint,
  isOnboardingLeadershipHint,
  isRole,
  ROLES,
} from "../../Utils/Constants/RoleConstants";
import { encryptWithPublicKey } from "../../Utils/Helper/clientEncryption";
import {
  getFromLocalStorage,
  saveToLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";

const ONBOARDING_PRIMARY_BTN_CLASS =
  "hover:!bg-gradient-to-r hover:!from-purple-600 hover:!via-indigo-600 hover:!to-violet-600 hover:shadow-md transition-all";

const normalizeEmail = (value: string) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isValidEmailFormat = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const generateSlug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const TEAM_ROLE_OPTIONS = ACCOUNT_TEAM_ALLOWED_ROLES.map(role => ({
  value: role,
  label: `${role} (${ACCOUNT_TEAM_ROLE_DISPLAY_LABELS[role]})`,
}));

const OnboardingWizard: React.FC = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, publicKey, isLoading, publicKeyLoading } =
    useAppSelector(state => state.authentication);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const isUserAuthenticated = isAuthenticated || hasAccessToken();
  const {
    wizardLoading,
    wizardError,
    wizardInvite: invite,
    wizardTenantDomain: tenantDomain,
    wizardAccounts: accounts,
    wizardCompleteLoading,
  } = useAppSelector(state => state.easyOnboarding);

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const tenantFromQuery = useMemo(
    () => searchParams.get("tenant") || "",
    [searchParams]
  );

  const [missingToken, setMissingToken] = useState(false);
  const [formError, setFormError] = useState("");
  const [profile, setProfile] = useState({
    email: "",
    name: "",
    title: "",
    phone: "",
    languages: "",
  });
  const [credentials, setCredentials] = useState({
    password: "",
    confirmPassword: "",
  });
  const [accountSearch, setAccountSearch] = useState("");
  const [monitorSearch, setMonitorSearch] = useState("");
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [selectedMonitoringAccountIds, setSelectedMonitoringAccountIds] =
    useState<string[]>([]);
  const [claimedTeamRole, setClaimedTeamRole] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const saving = wizardCompleteLoading;
  const loginLoading = isLoggingIn || isLoading || publicKeyLoading;
  const submitBusy = saving || loginLoading;
  const fatalError = missingToken
    ? "Missing invitation token"
    : wizardError || "";
  const loading = missingToken ? false : wizardLoading;
  const blockInstantCompleteUntilRef = useRef(0);

  const identityHint = invite?.company_identity_hint;
  const isAccountTeam = isOnboardingAccountTeamHint(identityHint);
  const isLeadershipInvite = isOnboardingLeadershipHint(identityHint);
  const showAssociationStep = isAccountTeam;
  const showMonitoringStep = isAccountTeam || isLeadershipInvite;
  const totalWizardSteps =
    showAssociationStep && showMonitoringStep ? 3 : showMonitoringStep ? 2 : 1;

  const isCompletionBlocked = () =>
    Date.now() < blockInstantCompleteUntilRef.current;

  const armCompletionBlock = () => {
    blockInstantCompleteUntilRef.current = Date.now() + 500;
  };

  const visibleAccounts = useMemo(() => {
    const q = accountSearch.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(account =>
      String(account.name || "")
        .toLowerCase()
        .includes(q)
    );
  }, [accountSearch, accounts]);

  const visibleMonitoringAccounts = useMemo(() => {
    const pool = accounts.filter(
      account =>
        !showAssociationStep || !selectedAccountIds.includes(account.id)
    );
    const q = monitorSearch.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(account =>
      String(account.name || "")
        .toLowerCase()
        .includes(q)
    );
  }, [accounts, monitorSearch, selectedAccountIds, showAssociationStep]);

  const stepTitle = useMemo(() => {
    if (currentStep === 0) return "Your profile";
    if (currentStep === 1 && showAssociationStep) return "Account portfolio";
    return isAccountTeam
      ? "Monitored accounts (optional)"
      : "Monitored accounts";
  }, [currentStep, showAssociationStep, isAccountTeam]);

  const stepSubtitle = useMemo(() => {
    if (currentStep === 0) {
      return "Tell us who you are so we can finish setting up your account.";
    }
    if (currentStep === 1 && showAssociationStep) {
      return "Select the account(s) for your portfolio. Account team members require at least one selection.";
    }
    return isAccountTeam
      ? "Optionally, you can choose additional accounts to monitor. Portfolio accounts are not shown."
      : "Select at least one account to monitor.";
  }, [currentStep, showAssociationStep, isAccountTeam]);

  const displayStepNumber =
    currentStep === 0
      ? 1
      : currentStep === 1 && showAssociationStep
        ? 2
        : totalWizardSteps;

  const isLastStep =
    (currentStep === 0 && !showAssociationStep && !showMonitoringStep) ||
    (currentStep === 1 && !showAssociationStep && showMonitoringStep) ||
    (currentStep === 2 && showAssociationStep);

  useEffect(() => {
    setSelectedMonitoringAccountIds(prev =>
      prev.filter(id => !selectedAccountIds.includes(id))
    );
  }, [selectedAccountIds]);

  useEffect(() => {
    if (!token) {
      setMissingToken(true);
      dispatch(clearWizardState());
      return;
    }

    setMissingToken(false);
    dispatch(
      loadOnboardingWizard({
        token,
        tenantDomain: tenantFromQuery || undefined,
      })
    );

    return () => {
      dispatch(clearWizardState());
    };
  }, [dispatch, token, tenantFromQuery]);

  const showFormError = (message: string) => {
    setFormError(message);
    toast.error(message);
  };

  const validateBasics = (): boolean => {
    const emailRaw = String(profile.email || "").trim();
    const fullName = String(profile.name || "").trim();
    const title = String(profile.title || "").trim();

    if (!emailRaw) {
      showFormError("Email is required");
      return false;
    }

    const emailNormalized = normalizeEmail(emailRaw);
    if (!isValidEmailFormat(emailNormalized)) {
      showFormError("Please enter a valid email address");
      return false;
    }

    const invitedNormalized = normalizeEmail(invite?.invited_email || "");
    if (emailNormalized !== invitedNormalized) {
      showFormError(
        "This is not the correct email address for the org you've been invited to join."
      );
      return false;
    }

    if (!fullName) {
      showFormError("Name is required");
      return false;
    }

    if (!title) {
      showFormError("Title is required");
      return false;
    }

    if (isAccountTeam && !claimedTeamRole) {
      showFormError("Please select your account team role");
      return false;
    }

    if (!isUserAuthenticated) {
      const password = String(credentials.password || "");
      const confirmPassword = String(credentials.confirmPassword || "");
      if (!password || !confirmPassword) {
        showFormError("Password and confirm password are required");
        return false;
      }
      if (password !== confirmPassword) {
        showFormError("Password confirmation does not match");
        return false;
      }
      if (password.length < 8) {
        showFormError("Password must be at least 8 characters");
        return false;
      }
    }

    return true;
  };

  const performLogin = async (email: string, password: string) => {
    setIsLoggingIn(true);
    try {
      let key = publicKey;
      if (!key) {
        const keyResult = await dispatch(fetchPublicKey());
        if (keyResult.meta.requestStatus === "fulfilled") {
          key = (keyResult.payload as string) || null;
        }
      }
      if (!key) {
        throw new Error("Unable to fetch encryption key. Please try again.");
      }

      const encryptedPassword = await encryptWithPublicKey(key, password);

      const result = await dispatch(
        loginUser({
          email: normalizeEmail(email),
          password: encryptedPassword,
        })
      );

      if (result.meta.requestStatus !== "fulfilled") {
        throw new Error(
          "Login failed after onboarding. Please sign in manually."
        );
      }

      const payload = result.payload as {
        token: { access_token: string };
        user: Record<string, unknown>;
      };

      const user = payload.user;
      const resolvedSlug =
        typeof user.slug === "string" && user.slug.trim()
          ? user.slug.trim()
          : typeof user.tenant_slug === "string"
            ? user.tenant_slug.trim()
            : generateSlug(String(user.tenant_name || ""));

      const userData = {
        userId: user.user_id,
        email: user.email,
        fullName: user.full_name,
        phone: user.phone,
        role: user.role,
        tenantDomain: user.tenant_domain,
        tenantId: user.tenant_id,
        isNewUser: user.is_new_user || false,
        tenantName: user.tenant_name,
        slug: resolvedSlug,
        executiveOps: user.executive_ops ?? false,
        executiveOpsApprove: user.executive_ops_approve ?? false,
        imageUrl:
          typeof user.image_url === "string" && user.image_url.trim()
            ? user.image_url.trim()
            : "",
        preferences:
          user.preferences && typeof user.preferences === "object"
            ? user.preferences
            : {},
      };

      setAccessToken(payload.token.access_token);
      saveToLocalStorage(STORAGE_KEYS.LOGGED_IN_USER, userData);

      const defaultRoute = getDefaultRouteForRole(String(user.role || ""));
      const postLoginPath = isRole(String(user.role || ""), ROLES.IZBA_ADMIN)
        ? defaultRoute
        : resolvedSlug
          ? `/${resolvedSlug}${defaultRoute}`
          : defaultRoute;

      navigate(postLoginPath, { replace: true });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const submitOnboarding = async () => {
    setFormError("");

    try {
      if (!validateBasics()) return;

      if (showAssociationStep && selectedAccountIds.length === 0) {
        showFormError("Please select at least one account association");
        return;
      }

      if (
        showMonitoringStep &&
        !isAccountTeam &&
        selectedMonitoringAccountIds.length === 0
      ) {
        showFormError("Please select at least one account to monitor");
        return;
      }

      const effectiveTeamRole = String(
        claimedTeamRole || invite?.team_role_hint || "AE"
      )
        .trim()
        .toUpperCase();
      const associationType = isAccountTeam
        ? effectiveTeamRole.toLowerCase()
        : "ae";

      const associations = selectedAccountIds.map(accountId => ({
        account_id: accountId,
        association_type: associationType,
        is_account_team: !!isAccountTeam,
      }));

      let encryptedPasswordPayload = {};
      if (!isUserAuthenticated) {
        let key = publicKey;
        if (!key) {
          const keyResult = await dispatch(fetchPublicKey());
          if (keyResult.meta.requestStatus === "fulfilled") {
            key = (keyResult.payload as string) || null;
          }
        }

        if (!key) {
          throw new Error("Unable to fetch encryption key. Please try again.");
        }

        const [encryptedPassword, encryptedConfirmPassword] = await Promise.all(
          [
            encryptWithPublicKey(key, credentials.password),
            encryptWithPublicKey(key, credentials.confirmPassword),
          ]
        );

        encryptedPasswordPayload = {
          password: encryptedPassword,
          confirm_password: encryptedConfirmPassword,
        };
      }

      const requestBody = {
        token,
        profile: {
          email: String(profile.email || "").trim(),
          name: String(profile.name || "").trim(),
          title: String(profile.title || "").trim(),
          phone: profile.phone || undefined,
          languages: profile.languages || undefined,
        },
        associations,
        monitoring_account_ids: showMonitoringStep
          ? selectedMonitoringAccountIds
          : undefined,
        ...encryptedPasswordPayload,
      };

      await dispatch(
        completeOnboardingWizard({
          payload: requestBody,
          tenantDomain: tenantDomain || undefined,
        })
      ).unwrap();
      toast.success("Onboarding completed successfully");

      if (!isUserAuthenticated) {
        await performLogin(profile.email, credentials.password);
        return;
      }

      const role = loggedInUser?.role || "";
      const slug = loggedInUser?.slug || "";
      const defaultRoute = getDefaultRouteForRole(role);
      const postLoginPath = isRole(role, ROLES.IZBA_ADMIN)
        ? defaultRoute
        : slug
          ? `/${slug}${defaultRoute}`
          : defaultRoute;
      navigate(postLoginPath, { replace: true });
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : "Failed to complete onboarding";
      showFormError(message);
    }
  };

  const handleNext = async () => {
    setFormError("");
    try {
      if (currentStep === 0) {
        if (!validateBasics()) return;
        if (showAssociationStep) {
          setCurrentStep(1);
        } else if (showMonitoringStep) {
          armCompletionBlock();
          setCurrentStep(1);
        } else {
          await submitOnboarding();
        }
        return;
      }

      if (currentStep === 1 && showAssociationStep) {
        if (selectedAccountIds.length === 0) {
          showFormError("Please select at least one account association");
          return;
        }
        armCompletionBlock();
        setCurrentStep(2);
        return;
      }

      if (currentStep === 1 && !showAssociationStep && showMonitoringStep) {
        if (!isAccountTeam && selectedMonitoringAccountIds.length === 0) {
          showFormError("Please select at least one account to monitor");
          return;
        }
        if (isCompletionBlocked()) return;
        await submitOnboarding();
        return;
      }

      if (currentStep === 2) {
        if (!isAccountTeam && selectedMonitoringAccountIds.length === 0) {
          showFormError("Please select at least one account to monitor");
          return;
        }
        if (isCompletionBlocked()) return;
        await submitOnboarding();
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Please fix the highlighted fields";
      showFormError(message);
    }
  };

  const handleBack = () => {
    setFormError("");
    if (currentStep <= 0) return;
    setCurrentStep(step => Math.max(0, step - 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-xl p-8 text-center">
          <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin" />
          <h2 className="text-lg font-semibold text-gray-900">
            Loading invitation
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Please wait while we verify your onboarding link.
          </p>
        </div>
      </div>
    );
  }

  if (fatalError) {
    const isLinkError = /expired|invalid|not found|no longer pending/i.test(
      fatalError
    );
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg bg-white border border-red-200 rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-rose-500 to-red-600 px-6 py-4">
            <h2 className="text-white text-lg font-semibold">
              Invitation issue
            </h2>
          </div>
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <p className="text-gray-900 font-medium">
              {isLinkError
                ? "This invitation link is no longer valid."
                : "Unable to verify invitation."}
            </p>
            <p className="mt-2 text-sm text-gray-600">
              {isLinkError
                ? "Please request a fresh invite from your admin and use the latest invitation email."
                : fatalError}
            </p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                Refresh
              </Button>
              <Button
                variant="primary"
                className={ONBOARDING_PRIMARY_BTN_CLASS}
                onClick={() => navigate("/login")}
              >
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loginLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-xl p-8 text-center">
          <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin" />
          <h2 className="text-lg font-semibold text-gray-900">
            Signing you in
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Please wait while we finish setting up your account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 p-4 md:p-10">
      <div className="max-w-4xl mx-auto">
        <div className="rounded-t-2xl overflow-hidden shadow-lg border border-purple-200/60">
          <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-violet-600 px-6 py-8 text-white">
            <h1 className="text-2xl md:text-3xl font-bold mt-1">
              Welcome to Executive Bridge. Complete your onboarding.
            </h1>
            <p className="text-purple-100 mt-3 text-sm md:text-base max-w-2xl">
              Invited by{" "}
              <strong className="text-white">
                {invite?.invited_by_name?.trim() || "your organization"}
              </strong>
            </p>
          </div>
        </div>

        <div className="bg-white rounded-b-2xl shadow-xl border border-t-0 border-gray-200 p-6 md:p-8">
          {totalWizardSteps > 1 ? (
            <div className="mb-8">
              <div className="relative flex items-center justify-between">
                <div className="pointer-events-none absolute left-[18px] right-[18px] top-1/2 flex h-0.5 -translate-y-1/2">
                  {Array.from({ length: totalWizardSteps - 1 }).map(
                    (_, segIdx) => {
                      const n = segIdx + 1;
                      const segmentDone = displayStepNumber > n;
                      return (
                        <div
                          key={`seg-${n}`}
                          className={`min-w-0 flex-1 rounded ${
                            segmentDone ? "bg-emerald-400" : "bg-gray-200"
                          }`}
                        />
                      );
                    }
                  )}
                </div>
                {Array.from({ length: totalWizardSteps }).map((_, idx) => {
                  const n = idx + 1;
                  const active = displayStepNumber === n;
                  const done = displayStepNumber > n;
                  return (
                    <div
                      key={n}
                      className={`relative z-10 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold tabular-nums transition-colors ${
                        done
                          ? "bg-emerald-500 text-white"
                          : active
                            ? "bg-purple-600 text-white ring-4 ring-purple-100 ring-offset-2"
                            : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {done ? "✓" : n}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <form
            className="space-y-6"
            onSubmit={event => {
              event.preventDefault();
              if (isCompletionBlocked()) return;
              void handleNext();
            }}
            autoComplete="off"
          >
            {currentStep === 0 ? (
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4 shadow-inner space-y-6">
                <StepPanelHeader title={stepTitle} description={stepSubtitle} />
                <CommonInput
                  label="Email"
                  name="onboarding_work_email"
                  type="email"
                  value={profile.email}
                  onChange={e =>
                    setProfile(prev => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="name@company.com"
                  required
                  infoText="Enter your email address"
                  autoComplete="email"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CommonInput
                    label="Name"
                    name="onboarding_name"
                    value={profile.name}
                    onChange={e =>
                      setProfile(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Enter your full name"
                    required
                  />
                  <CommonInput
                    label="Title"
                    name="onboarding_title"
                    value={profile.title}
                    onChange={e =>
                      setProfile(prev => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="Enter your title"
                    required
                  />
                </div>

                {isAccountTeam ? (
                  <Select
                    label="Your role"
                    name="claimed_team_role"
                    value={claimedTeamRole}
                    onChange={e => setClaimedTeamRole(e.target.value)}
                    options={TEAM_ROLE_OPTIONS}
                    placeholder="Select your role"
                    isShowPlaceHolder
                    required
                    infoText="Pick your current role from the list."
                  />
                ) : null}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CommonInput
                    label="Phone (optional)"
                    name="onboarding_phone"
                    value={profile.phone}
                    onChange={e =>
                      setProfile(prev => ({ ...prev, phone: e.target.value }))
                    }
                    placeholder="+1 555 123 4567"
                  />
                  <CommonInput
                    label="Languages (optional)"
                    name="onboarding_languages"
                    value={profile.languages}
                    onChange={e =>
                      setProfile(prev => ({
                        ...prev,
                        languages: e.target.value,
                      }))
                    }
                    placeholder="English, Spanish"
                    autoComplete="off"
                  />
                </div>

                {!isUserAuthenticated ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CommonInput
                      label="Create password"
                      name="onboarding_new_password"
                      type="password"
                      value={credentials.password}
                      onChange={e =>
                        setCredentials(prev => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                      placeholder="Minimum 8 characters"
                      required
                      autoComplete="new-password"
                    />
                    <CommonInput
                      label="Confirm password"
                      name="onboarding_confirm_new_password"
                      type="password"
                      value={credentials.confirmPassword}
                      onChange={e =>
                        setCredentials(prev => ({
                          ...prev,
                          confirmPassword: e.target.value,
                        }))
                      }
                      placeholder="Re-enter your password"
                      required
                      autoComplete="new-password"
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            {currentStep === 1 && showAssociationStep ? (
              <AccountPicker
                title={stepTitle}
                description={stepSubtitle}
                required
                searchValue={accountSearch}
                onSearchChange={setAccountSearch}
                accounts={visibleAccounts}
                selectedIds={selectedAccountIds}
                onSelectedChange={setSelectedAccountIds}
                allAccounts={accounts}
              />
            ) : null}

            {(currentStep === 2 && showAssociationStep && showMonitoringStep) ||
            (currentStep === 1 &&
              !showAssociationStep &&
              showMonitoringStep) ? (
              <AccountPicker
                title={stepTitle}
                description={stepSubtitle}
                required={!isAccountTeam}
                searchValue={monitorSearch}
                onSearchChange={setMonitorSearch}
                accounts={visibleMonitoringAccounts}
                selectedIds={selectedMonitoringAccountIds}
                onSelectedChange={setSelectedMonitoringAccountIds}
                allAccounts={visibleMonitoringAccounts}
              />
            ) : null}

            {formError ? (
              <p className="text-sm text-red-600">{formError}</p>
            ) : null}

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-gray-100">
              <Button
                type="button"
                variant="secondary"
                onClick={handleBack}
                disabled={currentStep === 0 || submitBusy}
                icon={<ChevronLeft className="h-4 w-4" />}
              >
                Back
              </Button>
              <div className="flex gap-2 justify-end">
                {isLastStep ? (
                  <Button
                    type="button"
                    variant="primary"
                    className={ONBOARDING_PRIMARY_BTN_CLASS}
                    onClick={() => void submitOnboarding()}
                    loading={submitBusy}
                    disabled={submitBusy}
                    icon={<CheckCircle2 className="h-4 w-4" />}
                  >
                    {saving
                      ? "Completing..."
                      : loginLoading
                        ? "Signing in..."
                        : "Complete onboarding"}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    className={ONBOARDING_PRIMARY_BTN_CLASS}
                    onClick={() => void handleNext()}
                    loading={submitBusy}
                    disabled={submitBusy}
                  >
                    Continue
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const StepPanelHeader: React.FC<{
  title: string;
  description: string;
  required?: boolean;
}> = ({ title, description, required }) => (
  <div>
    <h3 className="text-base font-semibold text-gray-900">
      {title}
      {required ? <span className="text-red-500"> *</span> : null}
    </h3>
    <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">
      {description}
    </p>
  </div>
);

interface AccountPickerProps {
  title: string;
  description: string;
  required?: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  accounts: WizardAccount[];
  selectedIds: string[];
  onSelectedChange: (ids: string[]) => void;
  allAccounts: WizardAccount[];
}

const AccountPicker: React.FC<AccountPickerProps> = ({
  title,
  description,
  required,
  searchValue,
  onSearchChange,
  accounts,
  selectedIds,
  onSelectedChange,
  allAccounts,
}) => {
  return (
    <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4 shadow-inner">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
        <StepPanelHeader
          title={title}
          description={description}
          required={required}
        />
        <div className="flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={() =>
              onSelectedChange(allAccounts.map(account => account.id))
            }
            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={() => onSelectedChange([])}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>
      <div className="relative mb-3">
        <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={searchValue}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Search accounts..."
          className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg"
        />
      </div>
      <div className="border border-gray-200 rounded-xl max-h-72 overflow-y-auto divide-y">
        {accounts.length === 0 ? (
          <div className="px-3 py-6 text-sm text-gray-500 text-center">
            No accounts found.
          </div>
        ) : (
          accounts.map(account => {
            const checked = selectedIds.includes(account.id);
            return (
              <label
                key={account.id}
                className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-emerald-50/60"
              >
                <span className="text-sm text-gray-900">{account.name}</span>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={e => {
                    onSelectedChange(
                      e.target.checked
                        ? [...selectedIds, account.id]
                        : selectedIds.filter(id => id !== account.id)
                    );
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
              </label>
            );
          })
        )}
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Selected: {selectedIds.length} account(s)
      </p>
    </div>
  );
};

export default OnboardingWizard;
